import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { BarTitleContext } from './bar-title.js'

/**
 * Holds the name the bar is showing.
 *
 * Split from the context the way `ScaleProvider` is split from
 * `scale-context.ts`: the context is a type and a default, and this owns the
 * state. Both layouts wrap themselves in it — the ordinary one and the host
 * display — because the rule is one rule, and `AppHeader` is one header.
 *
 * It has to sit *above* `AppHeader` so the bar can read what it is called, and
 * above `<Outlet />` so the screen inside can say.
 */
export function BarTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)

  /*
   * Stable, because `useBarTitle` lists `show` among its dependencies. A new
   * function every render would re-run that effect every render — setting the
   * same title over and over, and re-rendering everything under the provider
   * each time it did.
   */
  const show = useCallback((next: string | null) => setTitle(next), [])
  const value = useMemo(() => ({ title, show }), [title, show])

  return <BarTitleContext.Provider value={value}>{children}</BarTitleContext.Provider>
}
